<template>
    <div v-loading.fullscreen.lock="fullscreenLoading" class="campaign-create">
        <div class="settings-field">
            <div class="basic-settings">
                <Name v-model:name="name" v-model:campaignApiId="campaignApiId"
                    :show-campaign-id-input="moderationMode" label="Название кампании"
                    placeholder="Название кампании" />
                <!-- <Status v-model:status="status" /> -->
                <Period v-model:period="period" label="Период кампании" />

                <!-- Рекламодатель и Бренд -->
                <div class="campaign-inline-pair">
                    <div class="targeting-block campaign-inline-pair__item">
                        <div class="title">
                            <span>Рекламодатель</span>
                        </div>
                        <div class="input-data">
                            <select v-model="advertiserId" class="campaign-name advertiser-select"
                                @change="onAdvertiserChange">
                                <option value="">{{ advertisers && advertisers.length ? 'Выберите рекламодателя' :
                                    'Рекламодатели не найдены' }}</option>
                                <option v-for="advertiser in advertisers" :key="advertiser.id" :value="advertiser.id">
                                    {{ advertiser.name }}
                                </option>
                            </select>
                            <div v-if="(!advertisers || advertisers.length === 0)" class="muted" style="margin-top:8px;">
                                У вас нет доступных рекламодателей. Обратитесь на
                                <a href="mailto:sales@2digital.tech">sales@2digital.tech</a>
                                или создайте рекламодателя.
                            </div>
                        </div>
                    </div>

                    <div class="targeting-block campaign-inline-pair__item">
                        <div class="title">
                            <span>Бренд</span>
                        </div>
                        <div class="input-data">
                            <select v-model="brandId" class="campaign-name">
                                <option value="">{{ availableBrands && availableBrands.length ? 'Выберите бренд' :
                                    'Бренды не найдены' }}</option>
                                <option v-for="brand in availableBrands" :key="brand.id" :value="brand.id">
                                    {{ brand.name }}
                                </option>
                            </select>
                            <div v-if="(!availableBrands || availableBrands.length === 0)" class="muted"
                                style="margin-top:8px;">
                                Для выбранного рекламодателя бренды не найдены.
                            </div>
                        </div>
                    </div>
                </div>

                <Comment v-model:comment="comment" />

                <!-- Создание: одна кнопка -->
                <button v-if="!isEditMode" @click="submitCampaign" class="submit">Сохранить</button>
                <!-- Редактирование: одна кнопка -->
                <button v-else-if="!moderationMode" @click="saveCampaign" class="submit">Сохранить</button>
                <!-- Модерация (админ): две кнопки -->
                <div v-else class="action-buttons">
                    <button @click="saveCampaign" class="submit submit--secondary">Сохранить</button>
                    <button @click="saveAndApprove" class="submit submit--primary">Сохранить и отправить</button>
                </div>
            </div>

            <div class="system-settings">
                <GoogleMap
                    :frames="framesWithPackageInfo || []"
                    v-model:selectedFrameGids="selectedFrameGids"
                    :can-manage-test-frames="isAdmin"
                    :show-organization-filter="isAdmin || isAgency"
                />
                <Options :frames="framesWithPackageInfo || []" :selectedFrameGids="selectedFrameGids" :campaignPeriod="period"
                    v-model:campaignFrames="campaignFrames" />
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

import Name from '../../common/Name.vue';
import Period from '../../common/Period.vue';
import Comment from '../../common/Comment.vue';
import Options from './Options.vue';
import GoogleMap from '../../common/GoogleMap.vue';
import { confirmAction } from '../../../composables/useConfirm';
import { getRtbDateString } from '../../../utils/timezones';

export default {
    name: 'CampaignCreate',
    components: { Name, Period, Comment, Options, GoogleMap },
    props: {
        user: {
            type: Object,
            default: null,
        },
        frames: {
            type: Array,
            default: () => [],
        },
        campaign: {
            type: Object,
            default: null,
        },
        advertisers: {
            type: Array,
            default: () => [],
        },
        brands: {
            type: Array,
            default: () => [],
        },
        moderationMode: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        isAdmin() {
            return !!(this.user && this.user.is_admin);
        },
        isAgency() {
            return !!(this.user && this.user.is_agency);
        },
        framesWithPackageInfo() {
            // Algorithm:
            // 1) Start from props.frames
            // 2) Keep only frames with ssp === 'cityscreen' and at least one price in frame.prices
            // 3) Annotate with packageNames/isInPackage (packages loaded separately)
            const framesArr = Array.isArray(this.frames) ? this.frames : [];
            // Filter by SSP and presence of prices
            const filteredFrames = framesArr.filter(f => {
                return f && String(f.ssp) === 'cityscreen' && Array.isArray(f.prices) && f.prices.length > 0;
            });

            const packagesArr = Array.isArray(this.packages) ? this.packages : [];

            if (!packagesArr.length) {
                return filteredFrames.map(f => ({
                    ...f,
                    packageNames: [],
                    isInPackage: false,
                }));
            }

            const gidToPackages = new Map();
            for (const p of packagesArr) {
                (p.frames || []).forEach(f => {
                    const gid = String(f.gid);
                    gidToPackages.set(gid, (gidToPackages.get(gid) || []).concat(p.name));
                });
            }

            return filteredFrames.map(f => ({
                ...f,
                packageNames: gidToPackages.get(String(f.gid)) || [],
                isInPackage: (gidToPackages.get(String(f.gid)) || []).length > 0,
            }));
        },
        isEditMode() {
            return !!this.campaign && !!this.campaign.id;
        },
        campaignIdForSubmit() {
            return this.isEditMode ? this.campaign.id : null;
        },
        editSubmitUrl() {
            if (!this.campaign) return '';
            if (this.campaign.campaign_external_id) {
                return `/guaranteed/campaigns/external/${this.campaign.campaign_external_id}/edit`;
            }
            return `/guaranteed/campaigns/${this.campaign.id}/edit`;
        },
        availableBrands() {
            if (!this.advertiserId) {
                return this.brands;
            }
            // Если выбран рекламодатель, показываем только его бренды
            const advertiser = this.advertisers.find(a => a.id === parseInt(this.advertiserId));
            if (advertiser && advertiser.brands) {
                return advertiser.brands;
            }
            return this.brands;
        },
    },
    data() {
        const today = getRtbDateString();
        return {
            fullscreenLoading: false,
            name: '',
            campaignId: '',
            status: 'approve',
            period: { start: today, end: '' },
            comment: '',
            selectedFrameGids: [],
            campaignFrames: [],
            advertiserId: '',
            brandId: '',
            campaignApiId: '',
            oidApiMap: {}, // mapping oid => api_id for moderation multi-operator flow
            externals: [], // existing externals (from server)
            // available screen packages for this user
            packages: [],
        };
    },
    mounted() {
        if (this.isEditMode) {
            this.$nextTick(() => {
                this.loadCampaignData();
            });
        }
        this.loadAvailablePackages();
        if (!this.isEditMode) this.applySoleIdentity();
    },

    watch: {
        campaign: {
            deep: true,
            immediate: true,
            handler(newVal) {
                if (newVal && newVal.id && this.isEditMode) {
                    this.$nextTick(() => {
                        this.loadCampaignData();
                    });
                }
            },
        },
        frames: {
            handler(newVal) {
                // frames prop updated
            },
            deep: true,
            immediate: true,
        },
        selectedFrameGids(newGids) {
            // Удаляем неактивные фреймы из выбранных
            const activeGids = newGids.filter(gid => {
                const frame = this.frames.find(f => f.gid === gid);
                return frame && frame.isActive;
            });

            if (activeGids.length !== newGids.length) {
                this.selectedFrameGids = activeGids;
                return;
            }

            // Автоматическая фильтрация по SSP уже происходит в GoogleMap.vue
            // Здесь просто обновляем campaignFrames
            this.campaignFrames = this.campaignFrames.filter((c) => newGids.includes(c.gid));
        },
    },

    methods: {
        async loadCampaignData() {
            if (!this.campaign) return;

            this.name = this.campaign.name || '';
            // Используем api_id если есть, иначе campaignId, иначе id
            this.campaignId = String(this.campaign.api_id || this.campaign.campaignId || this.campaign.id || '');
            this.status = this.campaign.status || 'approve';
            // Создаем новый объект для реактивности
            this.period = {
                start: this.campaign.period?.start || '',
                end: this.campaign.period?.end || '',
            };
            this.comment = this.campaign.comment || '';
            this.advertiserId = this.campaign.advertiser_id || '';
            this.brandId = this.campaign.brand_id || '';
            this.campaignApiId = this.campaign.api_id ? String(this.campaign.api_id) : '';
            this.selectedFrameGids = this.campaign.selectedFrameGids || [];
            this.campaignFrames = (this.campaign.campaignFrames || []).map((cf) => {
                // Находим соответствующий тариф по duration и repetitions
                const frame = this.frames.find((f) => f.gid === cf.gid);
                let selectedTariffId = '';

                if (frame && frame.prices && Array.isArray(frame.prices)) {
                    // Сначала ищем точное совпадение
                    let matchingTariff = frame.prices.find((tariff) => {
                        return (
                            Number(tariff.duration) === Number(cf.duration) &&
                            Number(tariff.repetitionPerDayBy) === Number(cf.frequency?.repetitions || 1)
                        );
                    });

                    // Если точного совпадения нет, ищем по duration
                    if (!matchingTariff) {
                        matchingTariff = frame.prices.find((tariff) => {
                            return Number(tariff.duration) === Number(cf.duration);
                        });
                    }

                    // Если и этого нет, берем первый доступный тариф
                    if (!matchingTariff && frame.prices.length > 0) {
                        matchingTariff = frame.prices[0];
                    }

                    if (matchingTariff) {
                        selectedTariffId = String(matchingTariff.id);
                    }
                }

                return {
                    gid: cf.gid || '',
                    frequency: cf.frequency || { repetitions: 1, loopDuration: 86400000 },
                    cost: cf.cost || 0,
                    baseCost: cf.baseCost ?? cf.cost ?? 0,
                    duration: cf.duration || 0,
                    selectedTariffId,
                    periods: Array.isArray(cf.periods) && cf.periods.length > 0 ? cf.periods : [],
                    timeSettings: Array.isArray(cf.timeSettings) ? cf.timeSettings : [],
                };
            });

            // Prepare oid -> api_id map for moderation mode (find oids from frames prop)
            if (this.moderationMode) {
                const oids = {};
                for (const cf of this.campaign.campaignFrames || []) {
                    const frame = this.frames.find((f) => f.gid === (cf.gid || cf.frame_gid));
                    const oid = frame ? (frame.oid || null) : null;
                    if (oid) {
                        oids[oid] = this.oidApiMap[oid] || null;
                    }
                }
                this.oidApiMap = oids;
            }
            // Fetch externals info from API to show retry/status
            if (this.moderationMode && this.isEditMode) {
                try {
                    const res = await axios.get(`/api/campaigns/${this.campaign.id}`);
                    const remote = res.data.campaign || {};
                    if (remote.externals && Array.isArray(remote.externals)) {
                        this.externals = remote.externals;
                        // ensure oidApiMap prefills from externals if missing
                        for (const ext of this.externals) {
                            if (ext.oid && ext.api_id && !this.oidApiMap[ext.oid]) {
                                this.oidApiMap[ext.oid] = String(ext.api_id);
                            }
                        }
                    }
                } catch (e) {
                    // ignore - non-fatal
                }
            }
            this.applySoleIdentity();
        },
        applySoleIdentity() {
            if (this.isAdmin) return;

            const advertisers = Array.isArray(this.advertisers) ? this.advertisers : [];
            if (!this.advertiserId && advertisers.length === 1) {
                this.advertiserId = advertisers[0].id;
            }

            const brands = this.availableBrands || [];
            if (this.advertiserId && !this.brandId && brands.length === 1) {
                this.brandId = brands[0].id;
            }
        },
        async retryExternal(ext) {
            const isConfirmed = await confirmAction({
                title: 'Отправить кампанию повторно?',
                message: `Внешняя кампания для OID ${ext.oid} будет отправлена в CityScreen ещё раз.`,
                confirmLabel: 'Отправить',
            });
            if (!isConfirmed) return;
            try {
                this.fullscreenLoading = true;
                const res = await axios.post(`/api/campaigns/${this.campaign.id}/externals/${ext.id}/retry`);
                if (res.data.success) {
                    this.$notify({ title: 'Успешно', message: 'Повтор отправки выполнен', type: 'success' });
                    // refresh externals
                    const reload = await axios.get(`/api/campaigns/${this.campaign.id}`);
                    this.externals = reload.data.campaign.externals || [];
                } else {
                    this.$notify({ title: 'Ошибка', message: res.data.error || 'Не удалось повторно отправить', type: 'error' });
                }
            } catch (e) {
                this.$notify({ title: 'Ошибка', message: e.response?.data?.error || 'Не удалось повторно отправить', type: 'error' });
            } finally {
                this.fullscreenLoading = false;
            }
        },
        async submitCampaign() {
            this.fullscreenLoading = true;
            try {
                if (!this.validateForm()) {
                    return;
                }

                const formData = this.buildFormData();

                const url = this.isEditMode
                    ? this.editSubmitUrl
                    : '/guaranteed/campaigns/create';

                await axios.post(url, formData);
                this.fullscreenLoading = false;
                this.$notify({
                    title: 'Выполнено!',
                    message: this.isEditMode
                        ? 'Кампания успешно обновлена'
                        : 'Кампания успешно создана и отправлена на модерацию',
                    type: 'success',
                });

                // После успешного сохранения перенаправляем на страницу кампаний
                setTimeout(() => {
                    window.location.href = '/guaranteed/campaigns';
                }, 1000);
            } catch (e) {
                this.fullscreenLoading = false;
                this.$notify({
                    title: 'Ошибка!',
                    message: e.response?.data?.error || 'Не удалось создать кампанию',
                    type: 'error',
                });
            } finally {
                this.fullscreenLoading = false;
            }
        },
        async loadAvailablePackages() {
            try {
                const res = await axios.get('/screen-packages/available');
                if (res && res.data && Array.isArray(res.data.packages)) {
                    this.packages = res.data.packages;
                } else {
                    this.packages = [];
                }
            } catch (e) {
                this.packages = [];
            }
        },
        async saveCampaign() {
            // Просто сохранить без отправки на модерацию
            this.fullscreenLoading = true;
            try {
                if (!this.validateForm()) {
                    return;
                }

                const formData = this.buildFormData();
                formData.approve = false; // Не отправлять на модерацию

                // API ID не требуется при обычном сохранении

                await axios.post(this.editSubmitUrl, formData);
                this.fullscreenLoading = false;
                this.$notify({
                    title: 'Выполнено!',
                    message: 'Кампания успешно сохранена',
                    type: 'success',
                });
            } catch (e) {
                this.fullscreenLoading = false;
                this.$notify({
                    title: 'Ошибка!',
                    message: e.response?.data?.error || 'Не удалось сохранить кампанию',
                    type: 'error',
                });
            } finally {
                this.fullscreenLoading = false;
            }
        },
        async saveAndApprove() {
            // Сохранить и отправить на модерацию
            this.fullscreenLoading = true;
            try {
                if (!this.validateForm()) {
                    return;
                }

                const formData = this.buildFormData();
                formData.approve = true; // Отправить на модерацию

                if (!this.campaignApiId || !this.campaignApiId.trim()) {
                    this.$notify({
                        title: 'Ошибка',
                        message: 'Необходимо указать API ID кампании для отправки на модерацию',
                        type: 'error',
                    });
                    this.fullscreenLoading = false;
                    return;
                }
                formData.api_id = parseInt(this.campaignApiId.trim(), 10);

                await axios.post(this.editSubmitUrl, formData);
                this.fullscreenLoading = false;
                this.$notify({
                    title: 'Выполнено!',
                    message: 'Кампания сохранена и отправлена на модерацию',
                    type: 'success',
                });

                // После успешного сохранения перенаправляем на страницу кампаний
                setTimeout(() => {
                    window.location.href = '/guaranteed/campaigns';
                }, 1000);
            } catch (e) {
                this.fullscreenLoading = false;
                this.$notify({
                    title: 'Ошибка!',
                    message: e.response?.data?.error || 'Не удалось сохранить и отправить кампанию',
                    type: 'error',
                });
            } finally {
                this.fullscreenLoading = false;
            }
        },
        validateForm() {
            // Валидация основных полей
            if (!this.name.trim()) {
                this.$notify({
                    title: 'Ошибка валидации',
                    message: 'Введите название кампании',
                    type: 'error',
                });
                this.fullscreenLoading = false;
                return false;
            }

            // Require advertiser selection (DB requires advertiser_id)
            if (!this.advertiserId) {
                this.$notify({
                    title: 'Ошибка валидации',
                    message: 'Выберите рекламодателя',
                    type: 'error',
                });
                this.fullscreenLoading = false;
                return false;
            }

            if (!this.period.start || !this.period.end) {
                this.$notify({
                    title: 'Ошибка валидации',
                    message: 'Выберите период кампании',
                    type: 'error',
                });
                this.fullscreenLoading = false;
                return false;
            }

            if (this.selectedFrameGids.length === 0) {
                this.$notify({
                    title: 'Ошибка валидации',
                    message: 'Выберите хотя бы один экран',
                    type: 'error',
                });
                this.fullscreenLoading = false;
                return false;
            }

            // Валидация: у каждого выбранного фрейма должен быть выбран тариф
            const missingTariff = this.campaignFrames.find((c) => !c.selectedTariffId);
            if (this.selectedFrameGids.length && missingTariff) {
                this.$notify({
                    title: 'Не выбран тариф',
                    message: `Выберите тариф для экрана ${missingTariff.gid}`,
                    type: 'warning',
                });
                this.fullscreenLoading = false;
                return false;
            }

            // Валидация периодов фреймов
            for (const frame of this.campaignFrames) {
                if (frame.periods && frame.periods.length > 0) {
                    for (const period of frame.periods) {
                        if (period.start < this.period.start || period.end > this.period.end) {
                            this.$notify({
                                title: 'Ошибка валидации',
                                message: `Период экрана ${frame.gid} не должен выходить за рамки периода кампании`,
                                type: 'error',
                            });
                            this.fullscreenLoading = false;
                            return false;
                        }
                    }
                }
            }

            return true;
        },
        buildFormData() {
            return {
                name: this.name,
                campaign_id: this.isEditMode
                    ? (this.campaignId.trim() ? parseInt(this.campaignId.trim(), 10) : null)
                    : null,
                advertiser_id: this.advertiserId ? parseInt(this.advertiserId, 10) : null,
                brand_id: this.brandId ? parseInt(this.brandId, 10) : null,
                status: this.status,
                period: this.period,
                comment: this.comment,
                updated: Date.now(),
                inventories: this.campaignFrames.map((c) => ({
                    gid: c.gid,
                    frequency: {
                        repetitions: c.frequency?.repetitions ?? 1,
                        loopDuration: c.frequency?.loopDuration ?? 86400000,
                    },
                    cost: c.cost,
                    baseCost: c.baseCost ?? c.cost,
                    duration: c.duration,
                    periods: c.periods || [],
                    timeSettings: c.timeSettings || [],
                })),
            };
        },
        onAdvertiserChange() {
            if (this.advertiserId) {
                const advertiser = this.advertisers.find(a => a.id === parseInt(this.advertiserId, 10));
                if (advertiser && advertiser.brands) {
                    const brandIds = advertiser.brands.map(b => b.id);
                    if (this.brandId && !brandIds.includes(parseInt(this.brandId, 10))) {
                        this.brandId = '';
                    }
                }
            }
            if (!this.isAdmin && this.advertiserId && !this.brandId && (this.availableBrands || []).length === 1) {
                this.brandId = this.availableBrands[0].id;
            }
        },
    },
};
</script>

<style scoped lang="scss">
// Стиль для селекта рекламодателя
.targeting-block .input-data select.campaign-name.advertiser-select {
    max-width: none;
    width: 100%;
}
</style>
